import { getRuntimeAppBasePath } from '@/lib/appUrl';

export const SIGNUP_SUCCESS_ROUTE = '/signup-success';
export const QUICK_WALKTHROUGH_ROUTE = '/walkthrough';
export const PENDING_SIGNUP_SUCCESS_ROUTE_KEY = 'pending_signup_success_route';

const ROUTE_LABELS: Record<string, string> = {
  '/': 'Home',
  '/dashboard': 'Dashboard',
  '/walkthrough': 'Quick walkthrough',
  '/signup-success': 'Account ready',
  '/create': 'Create',
  '/create/fresh': 'Start from scratch',
  '/create/sales': 'Product sales page',
  '/studio': 'Studio',
  '/usage': 'Usage history',
  '/credits': 'Buy credits',
  '/help': 'Help',
  '/diagnosis': 'Snapshot diagnosis',
  '/autopilot': 'Autopilot',
  '/index.html': 'Home',
  'dashboard.html': 'Dashboard',
  'quick-walkthrough-v3.html': 'Quick walkthrough',
  'quick-walkthrough-v6.html': 'Quick walkthrough',
  'autopilot-diagnosis-v2.html': 'Snapshot diagnosis',
  'autopilot-v2.html': 'Autopilot',
  'create-v3.html': 'Create',
  'create-v5.html': 'Create',
  'create-v3-fresh.html': 'Start from scratch',
  'create-v5-fresh.html': 'Start from scratch',
  'create-v3-sales.html': 'Product sales page',
  'studio-v2.html': 'Studio',
  'studio-v3.html': 'Studio',
  'credit.html': 'Buy credits',
  'usage.html': 'Usage history',
  'help.html': 'Help',
  'signup-success.html': 'Account ready',
};

export interface ParsedRedirectTarget {
  href: string;
  internalRoute: string | null;
  label: string;
  displayTarget: string;
}

export interface SignupSuccessActionContent {
  kind: 'product_sales' | 'page_creation' | 'default';
  kicker: string;
  title: string;
  description: string;
  ctaLabel: string;
}

const APP_ROUTE_PREFIXES = [
  '/',
  '/dashboard',
  '/walkthrough',
  '/create',
  '/create-v3',
  '/create-v3-fresh',
  '/create-v3-sales',
  '/studio',
  '/usage',
  '/credits',
  '/help',
  '/diagnosis',
  '/autopilot',
  '/autopilot-v2',
  '/autopilot-diagnosis-v2',
  '/signup-success',
  '/login',
  '/oauth/callback',
  '/404',
] as const;

const stripQueryAndHash = (value: string) => value.split('?')[0].split('#')[0];
const getEmbeddedHashRoute = (value: string) => {
  const hashRouteIndex = value.indexOf('#/');
  return hashRouteIndex >= 0 ? value.slice(hashRouteIndex + 1) : '';
};
const isSameOriginUrl = (url: URL) =>
  typeof window !== 'undefined' && url.origin === window.location.origin;
const normalizePathname = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed || trimmed === '/') return '/';

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return withLeadingSlash.replace(/\/+$/, '') || '/';
};
const getAuthRuntimeAppBasePath = () => {
  const configuredBasePath = getRuntimeAppBasePath();
  if (configuredBasePath) return configuredBasePath;
  if (typeof window === 'undefined') return '';

  const currentPathname = normalizePathname(window.location.pathname || '/');
  return currentPathname === '/' ? '' : currentPathname;
};
const isInsideRuntimeAppBasePath = (pathname: string) => {
  const basePath = normalizePathname(getAuthRuntimeAppBasePath() || '/');
  if (basePath === '/') return true;

  const normalizedPathname = normalizePathname(pathname);
  return normalizedPathname === basePath || normalizedPathname.startsWith(`${basePath}/`);
};
const getInternalRouteFromAppPath = (pathname: string, search = '') => {
  const basePath = normalizePathname(getAuthRuntimeAppBasePath() || '/');
  const normalizedPathname = normalizePathname(pathname);
  const routePath =
    basePath !== '/' && normalizedPathname.startsWith(basePath)
      ? normalizedPathname.slice(basePath.length) || '/'
      : normalizedPathname;

  return `${routePath}${search}`;
};
const isKnownAppRoute = (value: string) => {
  const normalized = stripQueryAndHash(value) || '/';
  return APP_ROUTE_PREFIXES.some((route) =>
    route === '/' ? normalized === '/' : normalized === route || normalized.startsWith(`${route}/`),
  );
};

const humanizePath = (value: string) =>
  value
    .replace(/\.html$/i, '')
    .split(/[-_/]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const getRouteLabel = (value: string) => {
  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      const routeSource = url.hash.startsWith('#/') ? url.hash.slice(1) : url.pathname;
      const normalizedUrlPath = stripQueryAndHash(routeSource) || '/';
      const file = normalizedUrlPath.split('/').filter(Boolean).pop() || '';

      if (url.hash.startsWith('#/') && ROUTE_LABELS[normalizedUrlPath]) {
        return ROUTE_LABELS[normalizedUrlPath];
      }

      if (!url.hash.startsWith('#/') && normalizedUrlPath === '/') {
        return url.hostname.replace(/^www\./, '');
      }

      if (ROUTE_LABELS[normalizedUrlPath]) return ROUTE_LABELS[normalizedUrlPath];
      if (ROUTE_LABELS[file]) return ROUTE_LABELS[file];

      return humanizePath(file || url.hostname.replace(/^www\./, '')) || 'Page';
    } catch {
      // Continue with the generic path parser below.
    }
  }

  const normalized = stripQueryAndHash(value);
  if (ROUTE_LABELS[normalized]) return ROUTE_LABELS[normalized];

  const file = normalized.split('/').filter(Boolean).pop() || '';
  if (ROUTE_LABELS[file]) return ROUTE_LABELS[file];

  return humanizePath(file || normalized || 'page') || 'Page';
};

const extractInternalRoute = (value: string) => {
  if (value.startsWith('/')) return value;
  if (value.startsWith('#/')) return value.slice(1);

  if (!/^https?:\/\//i.test(value)) {
    return '';
  }

  try {
    const url = new URL(value);
    if (!isSameOriginUrl(url)) {
      return '';
    }
    if (url.hash.startsWith('#/')) {
      return url.hash.slice(1);
    }
    if (!isInsideRuntimeAppBasePath(url.pathname)) {
      return '';
    }
    if (!/\.[^/]+$/.test(url.pathname)) {
      return getInternalRouteFromAppPath(url.pathname, url.search);
    }
    return '';
  } catch {
    return '';
  }
};

export const normalizeRedirectTarget = (raw?: string | null) => {
  const value = raw?.trim() || '';
  if (!value) return '';
  if (/^(javascript|data):/i.test(value)) return '';
  if (value.startsWith('//')) return '';
  if (value.startsWith('/')) return getEmbeddedHashRoute(value) || value;
  if (value.startsWith('#/')) return value.slice(1);
  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      if (!isSameOriginUrl(url)) {
        return url.toString();
      }

      const embeddedHashRoute = getEmbeddedHashRoute(url.hash);
      if (embeddedHashRoute) {
        return embeddedHashRoute;
      }

      if (!isInsideRuntimeAppBasePath(url.pathname)) {
        return url.toString();
      }

      if (!/\.[^/]+$/.test(url.pathname)) {
        return getInternalRouteFromAppPath(url.pathname, url.search);
      }

      return url.toString();
    } catch {
      return '';
    }
  }
  return '';
};

export const parseRedirectTarget = (raw?: string | null): ParsedRedirectTarget | null => {
  const href = normalizeRedirectTarget(raw);
  if (!href) return null;

  const internalRoute = extractInternalRoute(href);
  const displayTarget = internalRoute || href;
  const label = getRouteLabel(internalRoute || href);

  return {
    href,
    internalRoute: internalRoute || null,
    label,
    displayTarget,
  };
};

export const resolveSignupSuccessRedirectTarget = (params?: {
  redirectTarget?: string | null;
  pendingSignupSuccessRoute?: string | null;
}) => {
  const directTarget = parseRedirectTarget(params?.redirectTarget);
  if (directTarget) return directTarget;

  const pendingRoute = params?.pendingSignupSuccessRoute?.trim() || '';
  if (pendingRoute) {
    const queryString = pendingRoute.split('?')[1] || '';
    const pendingRedirect = new URLSearchParams(queryString).get('redirect');
    const pendingTarget = parseRedirectTarget(pendingRedirect);
    if (pendingTarget) return pendingTarget;
  }

  return null;
};

export const shouldUseInternalRouteNavigation = (redirectTarget?: ParsedRedirectTarget | null) =>
  Boolean(redirectTarget?.internalRoute && isKnownAppRoute(redirectTarget.internalRoute));

export const getSignupSuccessActionContent = (
  redirectTarget: ParsedRedirectTarget | null,
): SignupSuccessActionContent | null => {
  if (!redirectTarget) return null;

  const internalRoute = redirectTarget.internalRoute || '';

  if (internalRoute.startsWith('/create/sales')) {
    return {
      kind: 'product_sales',
      kicker: 'Product landing page',
      title: 'Continue generating your product sales page.',
      description:
        'Go back to the product sales builder and continue from the product landing page flow.',
      ctaLabel: 'Continue product page generation',
    };
  }

  if (internalRoute.startsWith('/create/fresh')) {
    return {
      kind: 'page_creation',
      kicker: 'Landing page creation',
      title: 'Continue building your landing page.',
      description: 'Go back to the creation flow and keep shaping the page brief.',
      ctaLabel: 'Continue page creation',
    };
  }

  return {
    kind: 'default',
    kicker: 'Return to',
    title: redirectTarget.label,
    description: 'Pick up where you left off.',
    ctaLabel: `Return to ${redirectTarget.label}`,
  };
};

export const buildSignupSuccessRoute = (params?: { redirectTarget?: string | null }) => {
  const query = new URLSearchParams();

  if (params?.redirectTarget) {
    query.set('redirect', params.redirectTarget);
  }

  const queryString = query.toString();
  return queryString ? `${SIGNUP_SUCCESS_ROUTE}?${queryString}` : SIGNUP_SUCCESS_ROUTE;
};

export const rememberPendingSignupSuccessRoute = (route: string) => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(PENDING_SIGNUP_SUCCESS_ROUTE_KEY, route);
};

export const getPendingSignupSuccessRoute = () => {
  if (typeof window === 'undefined') return '';
  return window.sessionStorage.getItem(PENDING_SIGNUP_SUCCESS_ROUTE_KEY) || '';
};

export const clearPendingSignupSuccessRoute = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_SIGNUP_SUCCESS_ROUTE_KEY);
};

export const buildQuickWalkthroughRoute = (params?: { redirectTarget?: string | null }) => {
  const query = new URLSearchParams();

  if (params?.redirectTarget) {
    query.set('redirect', params.redirectTarget);
  }

  const queryString = query.toString();
  return queryString ? `${QUICK_WALKTHROUGH_ROUTE}?${queryString}` : QUICK_WALKTHROUGH_ROUTE;
};
